import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  LinearProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import moment from 'moment';
import { listMainBooks, setMainBookShowInApp, setMainBookSortOrder } from '../../services/api';

const isShownInApp = (value) => value !== false;

const ON_COLOR = '#2e7d32';
const OFF_COLOR = '#d32f2f';

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: ON_COLOR,
    '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' }
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: ON_COLOR,
    opacity: 0.6
  },
  '& .MuiSwitch-switchBase': {
    color: OFF_COLOR,
    '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' }
  },
  '& .MuiSwitch-switchBase + .MuiSwitch-track': {
    backgroundColor: OFF_COLOR,
    opacity: 0.6
  }
};

const sortRows = (rows) => {
  const nullsLast = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return 0;
  };
  return [...rows].sort((a, b) => {
    const aOn = isShownInApp(a.show_in_app);
    const bOn = isShownInApp(b.show_in_app);
    if (aOn !== bOn) return aOn ? -1 : 1;

    const sortOrderDiff = nullsLast(a.sort_order, b.sort_order);
    if (sortOrderDiff !== 0) return sortOrderDiff;
    if (a.sort_order != null && b.sort_order != null && a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    const releaseDiff = nullsLast(a.release_date, b.release_date);
    if (releaseDiff !== 0) return releaseDiff;
    if (!a.release_date || !b.release_date) return 0;
    return moment(a.release_date).valueOf() - moment(b.release_date).valueOf();
  });
};

const BookReleasesTable = ({ selectedDate, userStore }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (row) => (event) => {
    if (!isShownInApp(row.show_in_app)) {
      event.preventDefault();
      return;
    }
    setDraggedId(row.id);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (row) => (event) => {
    if (draggedId == null || !isShownInApp(row.show_in_app)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverId !== row.id) {
      setDragOverId(row.id);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const persistSortOrder = async (orderedRows) => {
    const items = orderedRows
      .filter((r) => isShownInApp(r.show_in_app))
      .map((r, idx) => ({ main_book_id: r.id, sort_order: idx }));
    try {
      await setMainBookSortOrder(items, userStore.token);
    } catch (e) {
      // ignore — UI keeps the new order optimistically
    }
  };

  const handleDrop = (targetRow) => (event) => {
    event.preventDefault();
    if (draggedId == null || draggedId === targetRow.id || !isShownInApp(targetRow.show_in_app)) {
      handleDragEnd();
      return;
    }
    const draggedIndex = rows.findIndex((r) => r.id === draggedId);
    const targetIndex = rows.findIndex((r) => r.id === targetRow.id);
    if (draggedIndex === -1 || targetIndex === -1) {
      handleDragEnd();
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(draggedIndex, 1);
    const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    next.splice(insertIndex, 0, moved);
    let order = 0;
    const reordered = next.map((r) => (
      isShownInApp(r.show_in_app) ? { ...r, sort_order: order++ } : r
    ));
    setRows(reordered);
    handleDragEnd();
    persistSortOrder(reordered);
  };

  const handleToggleShowInApp = async (row, nextValue) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, show_in_app: nextValue } : r)));
    try {
      const response = await setMainBookShowInApp(row.id, nextValue, userStore.token);
      const updated = response.data?.data ?? response.data;
      setRows((prev) => sortRows(prev.map((r) => (
        r.id === row.id
          ? {
              ...r,
              show_in_app: updated?.show_in_app ?? nextValue,
              sort_order: updated?.sort_order ?? null
            }
          : r
      ))));
    } catch (e) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, show_in_app: row.show_in_app } : r)));
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      setLoading(true);
      const filters = {
        releaseDateStart: selectedDate,
        releaseDateEnd: selectedDate
      };
      const order = { order: 'sort_order', sort: 'asc' };
      try {
        const firstResponse = await listMainBooks(1, filters, order, userStore.token);
        const firstPayload = firstResponse.data?.data;
        if (!firstPayload) {
          if (!cancelled) setRows([]);
          return;
        }

        let collected = firstPayload.data || [];
        const totalPages = firstPayload.total_pages || 1;

        const remainingPages = [];
        for (let page = 2; page <= totalPages; page += 1) {
          remainingPages.push(listMainBooks(page, filters, order, userStore.token));
        }
        const remainingResponses = await Promise.all(remainingPages);
        remainingResponses.forEach((response) => {
          const items = response.data?.data?.data;
          if (items) {
            collected = collected.concat(items);
          }
        });

        if (!cancelled) {
          setRows(sortRows(collected));
        }
      } catch (e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();

    return () => {
      cancelled = true;
    };
  }, [selectedDate, userStore.token]);

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '40px' }} />
              <TableCell>Autores</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Editora</TableCell>
              <TableCell>Infos G.</TableCell>
              <TableCell>Reviews</TableCell>
              <TableCell>Seller - Amazon</TableCell>
              <TableCell>Seller - Meli</TableCell>
              <TableCell style={{ width: '140px' }}>Exibir no app?</TableCell>
              <TableCell>Ordem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10}><LinearProgress /></TableCell>
              </TableRow>
            ) : rows.map((row, index) => {
              const on = isShownInApp(row.show_in_app);
              return (
              <TableRow
                hover
                key={row.id}
                draggable={on}
                onDragStart={handleDragStart(row)}
                onDragOver={handleDragOver(row)}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop(row)}
                sx={{
                  opacity: draggedId === row.id ? 0.4 : 1,
                  ...(dragOverId === row.id && draggedId != null && draggedId !== row.id
                    ? { borderTop: `2px solid ${ON_COLOR}` }
                    : {})
                }}
              >
                <TableCell style={{ width: '40px', cursor: on ? 'grab' : 'default' }}>
                  {on && (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <DragIndicatorIcon fontSize='small' />
                    </Box>
                  )}
                </TableCell>
                <TableCell>{row.authors}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.publisher}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.reviews_status}</TableCell>
                <TableCell>{row.amazon_status}</TableCell>
                <TableCell>{row.meli_status}</TableCell>
                <TableCell style={{ width: '140px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      checked={isShownInApp(row.show_in_app)}
                      onChange={(e) => handleToggleShowInApp(row, e.target.checked)}
                      sx={switchSx}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        ml: 1,
                        fontWeight: 600,
                        color: isShownInApp(row.show_in_app) ? ON_COLOR : OFF_COLOR
                      }}
                    >
                      {isShownInApp(row.show_in_app) ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {!on ? '-' : index + 1}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default BookReleasesTable;